import { useEffect, useState } from 'react';

import { StandaloneProject } from '../../entities/standalone-project';
import { listStandaloneProjects } from '../api/standalone';
import { auth } from '../firebase/firebase';
import useSocket from './socket-hook';

// Module-level cache, same pattern as use-trello-boards — several components (session picker,
// sidebar, chat view) need the same list.
let cache: StandaloneProject[] | null = null;
let inflight: Promise<StandaloneProject[]> | null = null;
const listeners = new Set<(projects: StandaloneProject[]) => void>();

function setCache(projects: StandaloneProject[]) {
  cache = projects;
  listeners.forEach((l) => l(projects));
}

// Call after a REST delete (or any mutation this hook can't otherwise observe) so every mounted
// consumer refreshes immediately instead of showing a stale list — creates/updates already arrive
// live via standaloneProjectUpdated, delete has no equivalent push yet (see
// standalone-contract.md §0).
export async function invalidateStandaloneProjectsCache() {
  cache = null;
  inflight = null;
  const fresh = await listStandaloneProjects();
  setCache(fresh);
}

// The full list of a user's standalone projects (GET /api/standalone/projects), kept live via
// standaloneProjectUpdated so a newly-created/edited project shows up without a refetch. Distinct
// from use-standalone-project.ts, which tracks only the project bound to the *current* bootstrap
// chat conversation.
export function useStandaloneProjects() {
  const userId = auth.currentUser?.uid ?? 'anonymous';
  const { on, off } = useSocket(userId);

  const [projects, setProjects] = useState<StandaloneProject[]>(cache ?? []);
  const [isLoading, setIsLoading] = useState(!cache);

  useEffect(() => {
    if (cache) {
      setProjects(cache);
      setIsLoading(false);
      return;
    }

    if (!inflight) {
      inflight = listStandaloneProjects()
        .then((result) => {
          setCache(result);
          return result;
        })
        .finally(() => {
          inflight = null;
        });
    }

    let cancelled = false;
    setIsLoading(true);
    inflight
      .then((result) => {
        if (!cancelled) setProjects(result);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // React to another component invalidating the cache (e.g. after a delete elsewhere).
  useEffect(() => {
    const listener = (next: StandaloneProject[]) => setProjects(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    const onUpdated = (data: { userId: string; project: StandaloneProject }) => {
      const prev = cache ?? [];
      const next = prev.some((p) => p._id === data.project._id)
        ? prev.map((p) => (p._id === data.project._id ? data.project : p))
        : [data.project, ...prev];
      setCache(next);
    };

    on('standaloneProjectUpdated', onUpdated);
    return () => off('standaloneProjectUpdated', onUpdated as (...args: unknown[]) => void);
  }, [userId, on, off]);

  return { projects, isLoading };
}
